import React, { FC, useMemo } from 'react';
import { Autocomplete, AutocompleteProps, Box, FormLabel, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import styles from './CustomAutoSelect.module.scss';

type CustomAutoSelectOption = { name: string };

const OPTION_ROW_SX = {
  height: 48,
  minHeight: 48,
  maxHeight: 48,
  boxSizing: 'border-box' as const,
  display: 'flex',
  alignItems: 'center',
  py: 0,
};

interface CustomAutoSelectProps {
  options: CustomAutoSelectOption[];
  customClass?: string;
  inputLabel: string;
  handleChange: (event: React.SyntheticEvent<Element, Event>, value: CustomAutoSelectOption | null) => void;
  onInputChange: (event: any, inputValue: any) => void;
  value?: CustomAutoSelectOption | null;
  placeholder?: string;
  loading?: boolean;
  disablePortal?: boolean;
  slotProps?: AutocompleteProps<CustomAutoSelectOption, false, false, false>['slotProps'];
}

const CustomAutoSelect: FC<CustomAutoSelectProps> = ({
  options,
  customClass,
  inputLabel,
  handleChange,
  value,
  onInputChange,
  placeholder,
  loading = false,
  disablePortal = false,
  slotProps,
}) => {
  const mergedSlotProps = useMemo(() => {
    const paperFromUser = slotProps?.paper as { sx?: object } | undefined;
    const listboxFromUser = slotProps?.listbox as { sx?: object } | undefined;

    return {
      ...slotProps,
      paper: {
        elevation: 0,
        ...slotProps?.paper,
        sx: {
          boxShadow: '0px 4px 14px 0px #0000001F',
          borderRadius: '10px',
          overflow: 'hidden',
          mt: 0.5,
          ...(paperFromUser?.sx ?? {}),
        },
      },
      listbox: {
        ...slotProps?.listbox,
        sx: {
          py: 0,
          ...(listboxFromUser?.sx ?? {}),
        },
      },
    };
  }, [slotProps]);

  return (
    <Box
      id={styles.customSelectContainer}
      className={customClass}
    >
      <FormLabel className={styles.customSelectLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>
      <Autocomplete<CustomAutoSelectOption, false, false, false>
        disablePortal={disablePortal}
        options={options}
        filterOptions={opts => opts}
        onChange={handleChange}
        onInputChange={onInputChange}
        className={styles.customInput}
        popupIcon={<ExpandMoreIcon />}
        value={value}
        loading={loading}
        slotProps={mergedSlotProps}
        getOptionLabel={(option: CustomAutoSelectOption) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        sx={{ width: '100%' }}
        renderOption={(props, option, state) => {
          const { key, ...optionLiProps } = props;
          const isLast = state.index === options.length - 1;
          return (
            <Box
              key={key}
              component="li"
              {...optionLiProps}
              sx={{
                ...OPTION_ROW_SX,
                borderBottom: isLast ? 'none' : '1px solid var(--system-stroke-buttons, #EAEEF8)',
              }}
            >
              {option.name}
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
          />
        )}
      />
    </Box>
  );
};

export default CustomAutoSelect;
